import React from 'react';
import { usePWA } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Download, X, Share, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import IOSInstallGuide from './IOSInstallGuide';

interface PWAInstallBannerProps {
  className?: string;
}

const PWAInstallBanner: React.FC<PWAInstallBannerProps> = ({ className }) => {
  const { isInstallable, isInstalled, installApp, showIOSInstallPrompt, dismissIOSPrompt, isIOS, isSafari, isIOSChrome } = usePWA();
  const [showGuide, setShowGuide] = React.useState(false);
  
  // Use sessionStorage to persist dismissal only for current session
  const [dismissed, setDismissed] = React.useState(() => {
    return sessionStorage.getItem('pwa-banner-dismissed') === 'true';
  });

  React.useEffect(() => {
    if (dismissed) {
      sessionStorage.setItem('pwa-banner-dismissed', 'true');
    } else {
      sessionStorage.removeItem('pwa-banner-dismissed');
    }
  }, [dismissed]);

  // For iOS, use the iOS-specific prompt logic
  if (isIOS) {
    if (!showIOSInstallPrompt || isInstalled) {
      return null;
    }

    // Different messages for different iOS browsers
    const getIOSMessage = () => {
      if (isIOSChrome) {
        return {
          title: "Switch to Safari",
          subtitle: "Open in Safari to install as app",
          action: "Copy URL and open in Safari"
        };
      }
      return {
        title: "Install QWS Portal",
        subtitle: "Tap Share then \"Add to Home Screen\"",
        action: "Add to Home Screen"
      };
    };

    const message = getIOSMessage();

    return (
      <div className={cn(
        "fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-b border-blue-200 backdrop-blur-sm shadow-sm",
        "dark:from-blue-950/50 dark:via-indigo-950/50 dark:to-blue-950/50 dark:border-blue-800",
        className
      )}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0 p-1.5 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                <Share className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col">
                  <span className="font-medium text-sm text-foreground">
                    {message.title}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <span>{message.subtitle}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {isIOSChrome && (
                <Button 
                  size="sm" 
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    // You could add a toast here to confirm URL was copied
                  }}
                  className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
                >
                  Copy URL
                </Button>
              )}
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => setShowGuide(true)}
                className="h-8 px-3 text-xs hover:bg-blue-100 dark:hover:bg-blue-900/50"
              >
                <HelpCircle className="h-3 w-3 mr-1" />
                How?
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={dismissIOSPrompt}
                className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/50"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Install Guide Modal */}
        {showGuide && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
            <IOSInstallGuide onClose={() => setShowGuide(false)} />
          </div>
        )}
      </div>
    );
  }

  // For non-iOS platforms, use the original logic
  if (isInstalled || !isInstallable || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleInstall = async () => {
    await installApp();
    handleDismiss(); // Auto-dismiss after installation attempt
  };

  return (
    <div className={cn(
      "fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-b border-blue-200 backdrop-blur-sm shadow-sm",
      "dark:from-blue-950/50 dark:via-indigo-950/50 dark:to-blue-950/50 dark:border-blue-800",
      className
    )}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 p-1.5 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
              <Download className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <span className="font-medium text-sm text-foreground">
                  Install QWS Portal
                </span>
                <span className="text-xs text-muted-foreground">
                  Add to home screen for quick access
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button 
              size="sm" 
              onClick={handleInstall}
              className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              Install
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={handleDismiss}
              className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
